import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import { SupportedFormData } from '../schemas/supportedFormSchema';

/**
 * Turns a stored "new supported" draft back into values this form can be reset with.
 *
 * The draft is written by the Nedarim personal area, where a coordinator can type every detail
 * in front of the supported but cannot attach the certificates the record needs. This is the
 * only place in the app that reads that payload.
 *
 * ⚠️ Everything here tolerates a partial, stale or malformed draft and never throws. A draft is
 * an unfinished form by definition, it may have been written by an older build, and it arrives
 * as free-form JSON. The worst outcome allowed is an empty form; losing the whole screen to a
 * bad draft is not.
 */

/**
 * ⚠️ THIS LIST IS A CONTRACT WITH ANOTHER CODEBASE, AND THE `satisfies` BELOW IS WHAT ENFORCES
 * IT.
 *
 * The draft is written by hatzdaka-nedarim (js/pages/new-supported.js) in this form's own field
 * names. Renaming a field here without renaming it there fails nowhere at runtime: the draft
 * still saves, and it opens here with that field quietly missing - so a coordinator retypes what
 * they already typed in front of the supported, and nobody sees an error.
 *
 * The `satisfies` makes that rename break THIS build instead. If a compile error brought you
 * here, the other side needs the same rename; that repo's test/draft-field-names.test.js holds
 * the mirror of this list.
 *
 * ⚠️ Absent on purpose, and must stay absent: paymentInfos, contactClassifications,
 * childrenDocuments and declarationsComplete.
 *
 * The first three carry File objects, which cannot survive JSON - and files are the entire
 * reason this draft exists. Letting a draft carry those keys would wipe whatever the user has
 * just picked on this screen. declarationsComplete is a statement someone makes on this screen
 * after reading what they are agreeing to; restoring a tick from another device would be
 * recording an agreement that was never given.
 */
const RESTORABLE_KEYS = ['contact', 'spouse', 'children'] as const satisfies readonly (keyof SupportedFormData)[];

/** Dates travel as ISO strings in JSON; the form holds Date objects. */
const toDate = (value: unknown): Date | undefined => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * ⚠️ Dates are converted per field rather than by walking the object, so a string that merely
 * looks like a date - a street name, an id - is never turned into one.
 */
const restoreContact = (raw: unknown): Record<string, unknown> | undefined => {
  if (!isObject(raw)) return undefined;
  const { birthDate, ...rest } = raw;
  const parsedBirthDate = toDate(birthDate);
  return parsedBirthDate ? { ...rest, birthDate: parsedBirthDate } : rest;
};

const restoreChildren = (raw: unknown): Record<string, unknown>[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  return raw.filter(isObject).map((child) => {
    const { birthDate, ...rest } = child;
    const parsedBirthDate = toDate(birthDate);
    return parsedBirthDate ? { ...rest, birthDate: parsedBirthDate } : rest;
  });
};

export const mapSupportedDraftToFormData = (
  draft: SupportRequestDraftDtoIn | null | undefined,
): Partial<SupportedFormData> | null => {
  if (!draft?.payloadJson) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(draft.payloadJson);
  } catch {
    return null;
  }

  if (!isObject(parsed)) return null;

  const values: Record<string, unknown> = {};

  for (const key of RESTORABLE_KEYS) {
    const raw = parsed[key];
    if (raw === undefined || raw === null) continue;

    if (key === 'contact' || key === 'spouse') {
      const restored = restoreContact(raw);
      if (restored) values[key] = restored;
      continue;
    }

    const restored = restoreChildren(raw);
    if (restored) values[key] = restored;
  }

  return values as Partial<SupportedFormData>;
};

/** The name to show on a draft row, before the record exists to be named by. */
export const describeSupportedDraft = (draft: SupportRequestDraftDtoIn): string => {
  const values = mapSupportedDraftToFormData(draft);
  const contact = values?.contact as { firstName?: string; lastName?: string } | undefined;
  return `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`.trim();
};
